/**
 * Kvalitetsgrind för fordonssidor (/fordon/<slug>).
 *
 * Samma princip som övriga programmatiska familjer: en annonssida får bara
 * indexeras när den bär tillräckligt med UNIK, faktabaserad text. Texten är
 * densamma som besökaren ser (annonstext + expertbedömning + kontrollpunkter
 * + driftkostnad), så grinden mäter verkligt innehåll — inte metadata.
 */

import { assessPage, robotsMeta, type QualityAssessment } from "./seo-quality";
import { buildVehicleExpert, type VehicleLike } from "./vehicle-expert";

const OVERRIDES = { minWords: 300, minDataPoints: 8, maxSimilarity: 0.72 } as const;

/** All indexerbar brödtext på fordonssidan, som en sträng. */
export function vehicleSeoText(v: VehicleLike): string {
  const e = buildVehicleExpert(v);
  return [
    v.title,
    v.description ?? "",
    e.verdict,
    ...e.pros,
    ...e.cons,
    ...e.suitedFor,
    ...e.notSuitedFor,
    ...e.checks.map((c) => `${c.label} ${c.value} ${c.note}`),
    ...e.ownerCosts.map((c) => `${c.label} ${c.value} ${c.note}`),
    ...(v.equipment ?? []),
    v.price != null ? `${v.price} kr` : "",
    v.year != null ? `${v.year}` : "",
    v.mileage != null ? `${v.mileage * 10} km` : "",
    v.totalWeight != null ? `${v.totalWeight} kg` : "",
    v.serviceWeight != null ? `${v.serviceWeight} kg` : "",
    e.payload != null ? `${e.payload} kg lastvikt` : "",
  ]
    .filter(Boolean)
    .join(" ");
}

/** Bedömning mot syskonfordon i samma kategori. */
export function assessVehicle(v: VehicleLike, peers: VehicleLike[] = []): QualityAssessment {
  const peerTexts = peers
    .filter((p) => p !== v && p.category === v.category)
    .slice(0, 40)
    .map(vehicleSeoText);
  return assessPage(vehicleSeoText(v), peerTexts, OVERRIDES);
}

export function vehicleRobotsMeta(v: VehicleLike, peers: VehicleLike[] = []) {
  return robotsMeta(assessVehicle(v, peers));
}

export function vehicleIndexable(v: VehicleLike, peers: VehicleLike[] = []): boolean {
  return assessVehicle(v, peers).passed;
}
